// src/components/padrekko/qc/ParticipantsModal.jsx
//
// Owner-managed participant list for the loaded case. Rendered by
// CaseBanner when the Users icon is clicked.
//
// Read-visible to every participant; add-controls are owner-only
// (caseRole === "dh_analyst"). v0 input is a raw UUID paste — small,
// developer-grade, scales as the org pool grows without needing an
// /orgs endpoint. the server-joined org name + "Your org" cover the display side for
// the current demo flow.
//
// Backend has only POST /participants today — no DELETE / PATCH, so no
// remove control. v1 case-creation may leave slots empty and add later;
// the Add UI exists already for that path even though current case
// creation always fills both slots.

import { useState } from "react";
import { Loader2, X as XIcon, Plus, AlertCircle } from "lucide-react";
import { useQcStore } from "../../../stores/useQcStore";
import { usePlatformOrgContext } from "../../../stores/usePlatformOrgContext";

// Imported, never re-declared. CASE_ROLE_LABEL is owned by roleLabels.js and
// is in checkStageTablesSingleSource's NAMES, so a local copy fails the commit.
import { CASE_ROLE_LABEL } from "../../../../../admin-api/lib/padrekko-qc/roleLabels.js";

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const ROLE_ORDER = ["dh_analyst", "customer_analyst", "operator_analyst"];

// Friendly display for a participant's org. Three layers:
//   1. If this row matches the caller's role → prefer activeOrgName from
//      impersonation context, else "Your org".
//   2. org_name from the participants payload (server-joined from the
//      if not a demo org).
//   3. If orgDisplayName fell back to the UUID prefix, wrap with "Org ".
function displayOrg({ orgId, orgName, isYou, activeOrgName }) {
  if (isYou) return activeOrgName || "Your org";
  // Server-joined from the org row (see the participants query). The old
  // demoOrgs lookup returned a UUID prefix for anything outside the demo
  // three, which this component papered over by prefixing "Org ".
  return orgName || "Unknown org";
}

export default function ParticipantsModal({ isOpen, onClose }) {
  const caseInfo             = useQcStore((s) => s.caseInfo);
  const caseRole             = useQcStore((s) => s.caseRole);
  const addParticipant       = useQcStore((s) => s.addParticipant);
  const addParticipantInflight = useQcStore((s) => s.addParticipantInflight);
  const activeOrgName        = usePlatformOrgContext((s) => s.activeOrgName);

  // Which row is in "add" mode (one at a time). null = no add form open.
  const [addingRole, setAddingRole] = useState(null);
  const [orgIdInput, setOrgIdInput] = useState("");
  const [inlineError, setInlineError] = useState(null);

  if (!isOpen || !caseInfo) return null;

  const isOwner = caseRole === "dh_analyst";
  const participants = caseInfo.participants ?? [];
  const participantByRole = new Map(participants.map((p) => [p.role, p]));

  function startAdd(role) {
    setAddingRole(role);
    setOrgIdInput("");
    setInlineError(null);
  }

  function cancelAdd() {
    setAddingRole(null);
    setOrgIdInput("");
    setInlineError(null);
  }

  async function submitAdd() {
    const trimmed = orgIdInput.trim();
    if (!UUID_RE.test(trimmed)) {
      setInlineError("Invalid UUID format.");
      return;
    }
    if (trimmed === caseInfo.owner_org_id) {
      setInlineError("DH owner cannot also be a participant.");
      return;
    }
    // Other-slot collision: addingRole is the empty slot, the OTHER non-DH
    // role's org_id (if filled) must differ.
    const otherRole =
      addingRole === "customer_analyst" ? "operator_analyst" : "customer_analyst";
    const otherFilled = participantByRole.get(otherRole);
    if (otherFilled && otherFilled.org_id === trimmed) {
      setInlineError(
        // Migration 111: the same-tenant case is creatable, so this message must
        // point at it rather than read as a flat prohibition.
        "Customer and Operator must be different orgs. A case where one organization is both is created with the same-tenant option.");
      return;
    }
    setInlineError(null);
    const result = await addParticipant({ orgId: trimmed, role: addingRole });
    if (result.ok) {
      cancelAdd();
    } else {
      setInlineError(result.error || "Add failed.");
    }
  }

  return (
    <div
      className="fixed inset-0 z-[300] flex items-center justify-center bg-black/60 backdrop-blur-sm"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="w-full max-w-md bg-bg-soft border border-slate-700 rounded-xl shadow-2xl">
        <div className="px-5 py-3 border-b border-slate-800 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-gray-100">Participants</h3>
          <button
            type="button"
            onClick={onClose}
            className="text-fg-secondary hover:text-slate-200 transition"
            title="Close"
          >
            <XIcon size={14} />
          </button>
        </div>

        <div className="px-5 py-4 space-y-3">
          {ROLE_ORDER.map((role) => {
            const filled = participantByRole.get(role);
            const isMyRole = caseRole === role;
            const isAddingThisRow = addingRole === role;
            const canAdd =
              isOwner &&
              !filled &&
              role !== "dh_analyst"; // owner role isn't addable via this UI

            return (
              <div
                key={role}
                className="border border-slate-800 rounded-md px-3 py-2 bg-slate-900/40"
              >
                <div className="text-[9px] font-mono uppercase tracking-widest text-fg-secondary mb-0.5">
                  {CASE_ROLE_LABEL[role]}
                </div>

                {filled ? (
                  <>
                    <div className="text-[11px] text-slate-100 leading-snug">
                      {displayOrg({
                        orgId: filled.org_id,
                        orgName: filled.org_name,
                        isYou: isMyRole,
                        activeOrgName,
                      })}
                      {isMyRole && (
                        <span className="text-fg-secondary ml-1.5">(You)</span>
                      )}
                    </div>
                    {caseInfo.participant_users?.[role]?.name && (
                      <div className="text-[10px] text-fg-secondary mt-0.5">
                        Assigned: <span className="text-slate-300">{caseInfo.participant_users[role].name}</span>
                      </div>
                    )}
                  </>
                ) : isAddingThisRow ? (
                  <div className="space-y-1.5 mt-1">
                    <input
                      type="text"
                      value={orgIdInput}
                      onChange={(e) => {
                        setOrgIdInput(e.target.value);
                        if (inlineError) setInlineError(null);
                      }}
                      placeholder="Paste org UUID"
                      disabled={addParticipantInflight}
                      className="w-full text-[10px] font-mono px-2 py-1 rounded border
                                 border-slate-700 bg-slate-900 text-slate-100
                                 placeholder:text-slate-600
                                 focus:outline-none focus:border-slate-500
                                 disabled:opacity-50"
                    />
                    {inlineError && (
                      <div className="flex items-start gap-1 text-[10px] text-red-300 leading-snug">
                        <AlertCircle size={10} className="mt-0.5 shrink-0" />
                        <span>{inlineError}</span>
                      </div>
                    )}
                    <div className="flex gap-1.5 justify-end">
                      <button
                        type="button"
                        onClick={cancelAdd}
                        disabled={addParticipantInflight}
                        className="text-[10px] px-2 py-1 rounded border
                                   border-slate-700 text-slate-400
                                   hover:text-slate-200 hover:border-slate-600
                                   disabled:opacity-50 transition"
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        onClick={submitAdd}
                        disabled={addParticipantInflight || !orgIdInput.trim()}
                        className="flex items-center gap-1 text-[10px] px-2 py-1 rounded border
                                   bg-accent-action/20 border-accent-action/50 text-accent-action
                                   hover:bg-accent-action/30
                                   disabled:opacity-40 disabled:cursor-not-allowed
                                   transition"
                      >
                        {addParticipantInflight && (
                          <Loader2 size={10} className="animate-spin" />
                        )}
                        {addParticipantInflight ? "Adding…" : "Add"}
                      </button>
                    </div>
                  </div>
                ) : canAdd ? (
                  <button
                    type="button"
                    onClick={() => startAdd(role)}
                    className="flex items-center gap-1 text-[10px] px-2 py-0.5 rounded border
                               border-slate-700 text-slate-400
                               hover:text-slate-200 hover:border-slate-600 transition"
                  >
                    <Plus size={10} />
                    Add {CASE_ROLE_LABEL[role]}
                  </button>
                ) : (
                  <div className="text-[10px] text-fg-secondary italic">
                    {role === "dh_analyst" ? "—" : "Not yet assigned"}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <div className="px-5 py-3 border-t border-slate-800 flex justify-end">
          <button
            type="button"
            onClick={onClose}
            className="text-[11px] px-3 py-1.5 rounded border
                       border-slate-700 text-slate-300
                       hover:bg-slate-800 hover:border-slate-600 transition"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
